/**
 * Application Routes
 * Defines all routes for the application
 */

import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AuthGuard, FreightWriteGuard, GuestGuard, RoleGuard } from '../components/guards';
import { DashboardLayout } from '../components/layout';
import { REMITTANCE_PROFILES, REMITTANCE_STRUCTURE_TYPES } from '../hooks/useRemittances';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('../pages/Home'));
const LoginPage = lazy(() => import('../pages/auth/Login'));
const RegisterPage = lazy(() => import('../pages/auth/Register'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPassword'));
const CreatePasswordPage = lazy(() => import('../pages/auth/CreatePassword'));
const ForceChangePasswordPage = lazy(() => import('../pages/auth/ForceChangePassword'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
// Landing page of the Konoom hosted payment redirect (public, informative only)
const KonoomPaymentResultPage = lazy(
  () => import('../pages/payment/KonoomPaymentResult')
);

// Dashboard pages
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardHome'));

// Transporter pages
const TransportersListPage = lazy(() => import('../pages/dashboard/transporters/TransportersList'));
const DriversRegistryPage = lazy(() => import('../pages/dashboard/drivers/DriversRegistry'));
const AddTransporterPage = lazy(() => import('../pages/dashboard/transporters/AddTransporter'));
const TransporterDetailsPage = lazy(() => import('../pages/dashboard/transporters/TransporterDetails'));
const TransporterEditPage = lazy(() => import('../pages/dashboard/transporters/TransporterEdit'));

// Structure users pages
const StructureUsersListPage = lazy(() => import('../pages/dashboard/transporters/StructureUsersList'));
const AddStructureUserPage = lazy(() => import('../pages/dashboard/transporters/AddStructureUser'));
const EditStructureUserPage = lazy(() => import('../pages/dashboard/transporters/EditStructureUser'));
const UserRolesManagementPage = lazy(() => import('../pages/dashboard/transporters/UserRolesManagement'));

// Agencies pages
const AgenciesListPage = lazy(() => import('../pages/dashboard/transporters/AgenciesList'));
const AddAgencyPage = lazy(() => import('../pages/dashboard/transporters/AddAgency'));
const EditAgencyPage = lazy(() => import('../pages/dashboard/transporters/EditAgency'));

// My Structure pages (for connected user's structure)
const MyStructureInfoPage = lazy(() => import('../pages/dashboard/my-structure/MyStructureInfo'));
const MyStructureUsersPage = lazy(() => import('../pages/dashboard/my-structure/MyStructureUsers'));
const MyStructureAddUserPage = lazy(() => import('../pages/dashboard/my-structure/MyStructureAddUser'));
const MyAgenciesListPage = lazy(() => import('../pages/dashboard/my-structure/MyAgenciesList'));
const MyAgenciesAddPage = lazy(() => import('../pages/dashboard/my-structure/MyAgenciesAdd'));

// Configuration pages
const CountriesListPage = lazy(() => import('../pages/dashboard/configuration/CountriesList'));
const CountryAddPage = lazy(() => import('../pages/dashboard/configuration/CountryAdd'));
const CountryEditPage = lazy(() => import('../pages/dashboard/configuration/CountryEdit'));
const RegionsListPage = lazy(() => import('../pages/dashboard/configuration/RegionsList'));
const RegionAddPage = lazy(() => import('../pages/dashboard/configuration/RegionAdd'));
const RegionEditPage = lazy(() => import('../pages/dashboard/configuration/RegionEdit'));
const CitiesListPage = lazy(() => import('../pages/dashboard/configuration/CitiesList'));
const CityAddPage = lazy(() => import('../pages/dashboard/configuration/CityAdd'));
const CityEditPage = lazy(() => import('../pages/dashboard/configuration/CityEdit'));

// Configuration - Brands pages
const BrandsListPage = lazy(() => import('../pages/dashboard/configuration/BrandsList'));
const BrandAddPage = lazy(() => import('../pages/dashboard/configuration/BrandAdd'));
const BrandEditPage = lazy(() => import('../pages/dashboard/configuration/BrandEdit'));
const ModelsListPage = lazy(() => import('../pages/dashboard/configuration/ModelsList'));
const ModelAddPage = lazy(() => import('../pages/dashboard/configuration/ModelAdd'));
const ModelEditPage = lazy(() => import('../pages/dashboard/configuration/ModelEdit'));

// Configuration - Vehicle Types pages
const VehicleTypesListPage = lazy(() => import('../pages/dashboard/configuration/VehicleTypesList'));
const VehicleTypeAddPage = lazy(() => import('../pages/dashboard/configuration/VehicleTypeAdd'));
const VehicleTypeEditPage = lazy(() => import('../pages/dashboard/configuration/VehicleTypeEdit'));

// Configuration - Insurers pages
const InsurersListPage = lazy(() => import('../pages/dashboard/configuration/InsurersList'));
const InsurerAddPage = lazy(() => import('../pages/dashboard/configuration/InsurerAdd'));
const InsurerEditPage = lazy(() => import('../pages/dashboard/configuration/InsurerEdit'));

// Configuration - Categories pages
const CategoriesListPage = lazy(() => import('../pages/dashboard/configuration/CategoriesList'));
const CategoryAddPage = lazy(() => import('../pages/dashboard/configuration/CategoryAdd'));
const CategoryEditPage = lazy(() => import('../pages/dashboard/configuration/CategoryEdit'));

// Configuration - HS Codes pages
const HSCodesListPage = lazy(() => import('../pages/dashboard/configuration/HSCodesList'));
const HSCodeAddPage = lazy(() => import('../pages/dashboard/configuration/HSCodeAdd'));
const HSCodeEditPage = lazy(() => import('../pages/dashboard/configuration/HSCodeEdit'));

// Configuration - Shippers pages
const ShippersListPage = lazy(() => import('../pages/dashboard/configuration/ShippersList'));
const ShipperAddPage = lazy(() => import('../pages/dashboard/configuration/ShipperAdd'));
const ShipperEditPage = lazy(() => import('../pages/dashboard/configuration/ShipperEdit'));

// Configuration - Packaging Types pages
const PackagingTypesListPage = lazy(() => import('../pages/dashboard/configuration/PackagingTypesList'));
const PackagingTypeAddPage = lazy(() => import('../pages/dashboard/configuration/PackagingTypeAdd'));
const PackagingTypeEditPage = lazy(() => import('../pages/dashboard/configuration/PackagingTypeEdit'));

// Configuration - Container Types pages
const ContainerTypesListPage = lazy(() => import('../pages/dashboard/configuration/ContainerTypesList'));
const ContainerTypeAddPage = lazy(() => import('../pages/dashboard/configuration/ContainerTypeAdd'));
const ContainerTypeEditPage = lazy(() => import('../pages/dashboard/configuration/ContainerTypeEdit'));

// Configuration - Currencies pages
const CurrenciesListPage = lazy(() => import('../pages/dashboard/configuration/CurrenciesList'));
const CurrencyAddPage = lazy(() => import('../pages/dashboard/configuration/CurrencyAdd'));
const CurrencyEditPage = lazy(() => import('../pages/dashboard/configuration/CurrencyEdit'));

// Configuration - Parameters pages
const ParametersListPage = lazy(() => import('../pages/dashboard/configuration/ParametersList'));
const ParameterAddPage = lazy(() => import('../pages/dashboard/configuration/ParameterAdd'));
const ParameterEditPage = lazy(() => import('../pages/dashboard/configuration/ParameterEdit'));
const PaymentMethodsSettingsPage = lazy(() => import('../pages/dashboard/configuration/PaymentMethodsSettings'));

// Configuration - Corridors pages
const CorridorsListPage = lazy(() => import('../pages/dashboard/configuration/CorridorsList'));
const CorridorAddPage = lazy(() => import('../pages/dashboard/configuration/CorridorAdd'));
const CorridorEditPage = lazy(() => import('../pages/dashboard/configuration/CorridorEdit'));

// Configuration - Segments pages
const SegmentsListPage = lazy(() => import('../pages/dashboard/configuration/SegmentsList'));
const SegmentAddPage = lazy(() => import('../pages/dashboard/configuration/SegmentAdd'));
const SegmentEditPage = lazy(() => import('../pages/dashboard/configuration/SegmentEdit'));

// Configuration - Itineraries pages
const ItinerariesListPage = lazy(() => import('../pages/dashboard/configuration/ItinerariesList'));
const ItineraryAddPage = lazy(() => import('../pages/dashboard/configuration/ItineraryAdd'));
const ItineraryEditPage = lazy(() => import('../pages/dashboard/configuration/ItineraryEdit'));

// Configuration - Checkpoints pages
const CheckpointsListPage = lazy(() => import('../pages/dashboard/configuration/CheckpointsList'));
const CheckpointAddPage = lazy(() => import('../pages/dashboard/configuration/CheckpointAdd'));
const CheckpointEditPage = lazy(() => import('../pages/dashboard/configuration/CheckpointEdit'));

// Vehicle Fleet pages
const VehiclesListPage = lazy(() => import('../pages/dashboard/vehicles/VehiclesList'));
const AddVehiclePage = lazy(() => import('../pages/dashboard/vehicles/AddVehicle'));
const VehicleDetailsPage = lazy(() => import('../pages/dashboard/vehicles/VehicleDetails'));
const VehicleEditPage = lazy(() => import('../pages/dashboard/vehicles/VehicleEdit'));

// Freight Declaration pages
const FreightFormPage = lazy(() => import('../pages/dashboard/freight/FreightForm'));
const FreightListPage = lazy(() => import('../pages/dashboard/freight/FreightList'));
const WaybillsListPage = lazy(() => import('../pages/dashboard/freight/WaybillsList'));
const CarrierWaybillsListPage = lazy(() => import('../pages/dashboard/freight/CarrierWaybillsList'));
const DriverWaybillsListPage = lazy(() => import('../pages/dashboard/driver/DriverWaybillsList'));
const LvoAmendmentFormPage = lazy(() => import('../pages/dashboard/freight/LvoAmendmentForm'));
const LvoAmendmentHistoryPage = lazy(() => import('../pages/dashboard/freight/LvoAmendmentHistory'));
const AmendmentsHistoryListPage = lazy(() => import('../pages/dashboard/freight/AmendmentsHistoryList'));

// Finance pages
const InvoicesListPage = lazy(() => import('../pages/dashboard/finance/InvoicesList'));
const RemittancesListPage = lazy(() => import('../pages/dashboard/finance/RemittancesList'));
const RemittanceFormPage = lazy(() => import('../pages/dashboard/finance/RemittanceForm'));
const RemittanceTrackingPage = lazy(() => import('../pages/dashboard/finance/RemittanceTracking'));

// Checkpoint Management pages (Phase V)
const CheckpointPassagesPage = lazy(() => import('../pages/dashboard/checkpoint/CheckpointPassages'));
const CheckpointEventsPage = lazy(() => import('../pages/dashboard/checkpoint/CheckpointEvents'));
const OfflineSyncHistoryPage = lazy(() => import('../pages/dashboard/checkpoint/OfflineSyncHistory'));

// TAE pages (Phase V)
const TaeListPage = lazy(() => import('../pages/dashboard/tae/TaeList'));
const TaeVehicleControlPage = lazy(() => import('../pages/dashboard/tae/TaeVehicleControl'));
const TaeRenewalPage = lazy(() => import('../pages/dashboard/tae/TaeRenewal'));

// Admin pages
const EventLogsListPage = lazy(() => import('../pages/dashboard/admin/EventLogsList'));

// Échanges BGFT (Landfreightis data exchange tracking)
const BgftExchangesPage = lazy(() => import('../pages/dashboard/bgft/BgftExchanges'));

// Placeholder pages for future implementation
const PlaceholderPage = lazy(() =>
  Promise.resolve({
    default: () => (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Page en construction</h2>
        <p>Cette page sera bientôt disponible.</p>
      </div>
    ),
  })
);

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      {/* Konoom sends the customer back here after the hosted payment page.
          Public: the payer is not necessarily a platform user. */}
      <Route path="/payment/konoom/result" element={<KonoomPaymentResultPage />} />

      {/* Guest routes (only for non-authenticated users) */}
      <Route
        path="/login"
        element={
          <GuestGuard>
            <LoginPage />
          </GuestGuard>
        }
      />
      <Route
        path="/register"
        element={
          <GuestGuard>
            <RegisterPage />
          </GuestGuard>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <GuestGuard>
            <ForgotPasswordPage />
          </GuestGuard>
        }
      />
      <Route
        path="/reset-password"
        element={
          <GuestGuard>
            <ResetPasswordPage />
          </GuestGuard>
        }
      />
      <Route
        path="/create-password"
        element={
          <GuestGuard>
            <CreatePasswordPage />
          </GuestGuard>
        }
      />

      {/* Force change password route (authenticated but outside dashboard) */}
      <Route
        path="/force-change-password"
        element={
          <AuthGuard>
            <ForceChangePasswordPage />
          </AuthGuard>
        }
      />

      {/* Protected routes (require authentication) */}
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* Corridors */}
        <Route path="corridors/monitoring" element={<PlaceholderPage />} />

        {/* Structures */}
        <Route path="structures" element={<PlaceholderPage />} />

        {/* Transporters */}
        <Route path="transporters" element={<TransportersListPage />} />
        <Route path="transporters/add" element={<AddTransporterPage />} />
        <Route path="transporters/:id" element={<TransporterDetailsPage />} />
        <Route path="transporters/:id/edit" element={<TransporterEditPage />} />

        {/* Structure users */}
        <Route path="transporters/:id/users" element={<StructureUsersListPage />} />
        <Route path="transporters/:id/users/add" element={<AddStructureUserPage />} />
        <Route path="transporters/:id/users/:userId/edit" element={<EditStructureUserPage />} />
        <Route path="transporters/:id/users/:userId/roles" element={<UserRolesManagementPage />} />

        {/* Structure agencies */}
        <Route path="transporters/:id/agencies" element={<AgenciesListPage />} />
        <Route path="transporters/:id/agencies/add" element={<AddAgencyPage />} />
        <Route path="transporters/:id/agencies/:agencyId/edit" element={<EditAgencyPage />} />

        {/* Vehicle Fleet */}
        <Route path="transporters/vehicles" element={<VehiclesListPage />} />
        <Route path="transporters/vehicles/add" element={<AddVehiclePage />} />
        <Route path="transporters/vehicles/:id" element={<VehicleDetailsPage />} />
        <Route path="transporters/vehicles/:id/edit" element={<VehicleEditPage />} />

        {/* Freight Declaration — "Émission de lettre" is never open to carriers */}
        <Route
          path="freight/form"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <FreightWriteGuard>
                <FreightFormPage />
              </FreightWriteGuard>
            </RoleGuard>
          }
        />
        <Route
          path="freight/list"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <FreightListPage />
            </RoleGuard>
          }
        />
        <Route
          path="freight/waybills"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <WaybillsListPage />
            </RoleGuard>
          }
        />
        <Route
          path="freight/amendments"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <FreightWriteGuard>
                <AmendmentsHistoryListPage />
              </FreightWriteGuard>
            </RoleGuard>
          }
        />
        <Route
          path="freight/:freightId/lots/:lotId/amend"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <FreightWriteGuard>
                <LvoAmendmentFormPage />
              </FreightWriteGuard>
            </RoleGuard>
          }
        />
        <Route
          path="freight/:freightId/lots/:lotId/amendments"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <FreightWriteGuard>
                <LvoAmendmentHistoryPage />
              </FreightWriteGuard>
            </RoleGuard>
          }
        />

        {/* Carrier Waybills — a driver belongs to a carrier structure but must
            use the driver-scoped screen below, not the whole-fleet one. */}
        <Route
          path="transporters/waybills"
          element={
            <RoleGuard deniedUserTypes={['DRIVER']}>
              <CarrierWaybillsListPage />
            </RoleGuard>
          }
        />

        {/* Driver space — LVOs the connected user is assigned to drive */}
        <Route
          path="driver/waybills"
          element={
            <RoleGuard allowedUserTypes={['DRIVER']}>
              <DriverWaybillsListPage />
            </RoleGuard>
          }
        />

        {/* Finance */}
        <Route path="finance/invoices" element={<InvoicesListPage />} />

        {/* Aggregator remittances — OGF only, ADMIN / CASH_TRACKING / AGENCY_HEAD */}
        <Route
          path="finance/remittances"
          element={
            <RoleGuard allowedProfiles={REMITTANCE_PROFILES} requiredStructureTypes={REMITTANCE_STRUCTURE_TYPES}>
              <RemittancesListPage />
            </RoleGuard>
          }
        />
        <Route
          path="finance/remittances/new"
          element={
            <RoleGuard allowedProfiles={REMITTANCE_PROFILES} requiredStructureTypes={REMITTANCE_STRUCTURE_TYPES}>
              <RemittanceFormPage />
            </RoleGuard>
          }
        />
        <Route
          path="finance/remittances/tracking"
          element={
            <RoleGuard allowedProfiles={REMITTANCE_PROFILES} requiredStructureTypes={REMITTANCE_STRUCTURE_TYPES}>
              <RemittanceTrackingPage />
            </RoleGuard>
          }
        />

        {/* Checkpoint Management (Phase V) */}
        <Route path="checkpoint/passages" element={<CheckpointPassagesPage />} />
        <Route path="checkpoint/events" element={<CheckpointEventsPage />} />
        <Route path="checkpoint/sync-history" element={<OfflineSyncHistoryPage />} />

        {/* TAE (Phase V) — list & add/renewal restricted to ADMIN / TAE_EMITTER */}
        <Route
          path="tae/list"
          element={
            <RoleGuard
              allowedProfiles={['ADMIN', 'TAE_EMITTER', 'ACCOUNTING_OFFICER', 'GLOBAL_ACCOUNTING_OFFICER']}
              allowedStructureTypes={['CARRIER']}
            >
              <TaeListPage />
            </RoleGuard>
          }
        />
        <Route
          path="tae/renewal"
          element={
            <RoleGuard allowedProfiles={['ADMIN', 'TAE_EMITTER']} deniedStructureTypes={['CARRIER']}>
              <TaeRenewalPage />
            </RoleGuard>
          }
        />
        <Route
          path="tae/vehicle-control"
          element={
            <RoleGuard deniedStructureTypes={['CARRIER']}>
              <TaeVehicleControlPage />
            </RoleGuard>
          }
        />

        {/* Admin — global event history */}
        <Route
          path="admin/event-logs"
          element={
            <RoleGuard allowedProfiles={['ADMIN']} deniedStructureTypes={['CARRIER']}>
              <EventLogsListPage />
            </RoleGuard>
          }
        />

        {/* Échanges BGFT — ADMIN of an OGF structure only */}
        <Route
          path="bgft-exchanges"
          element={
            <RoleGuard allowedProfiles={['ADMIN']} requiredStructureTypes={['OGF']}>
              <BgftExchangesPage />
            </RoleGuard>
          }
        />

        {/* My Structure (connected user's structure) */}
        <Route path="my-structure/info" element={<MyStructureInfoPage />} />
        <Route path="my-structure/users" element={<MyStructureUsersPage />} />
        <Route path="my-structure/users/add" element={<MyStructureAddUserPage />} />
        <Route path="my-structure/agencies" element={<MyAgenciesListPage />} />
        <Route path="my-structure/agencies/add" element={<MyAgenciesAddPage />} />

        <Route path="vehicles" element={<PlaceholderPage />} />
        <Route path="vehicle-proposals" element={<PlaceholderPage />} />
        <Route path="voyages" element={<PlaceholderPage />} />
        <Route path="freight-agreements" element={<PlaceholderPage />} />

        {/* Drivers */}
        <Route path="my-drivers" element={<PlaceholderPage />} />
        <Route path="drivers" element={<PlaceholderPage />} />
        <Route path="drivers/registry" element={<DriversRegistryPage />} />

        {/* Freight owners */}
        <Route path="freight-owners" element={<PlaceholderPage />} />

        {/* Configuration - Checkpoints */}
        <Route path="configuration/checkpoints" element={<CheckpointsListPage />} />
        <Route path="configuration/checkpoints/add" element={<CheckpointAddPage />} />
        <Route path="configuration/checkpoints/:id/edit" element={<CheckpointEditPage />} />

        {/* Offers */}
        <Route path="offers" element={<PlaceholderPage />} />

        {/* Configuration - Countries */}
        <Route path="configuration/countries" element={<CountriesListPage />} />
        <Route path="configuration/countries/add" element={<CountryAddPage />} />
        <Route path="configuration/countries/:id/edit" element={<CountryEditPage />} />

        {/* Configuration - Regions */}
        <Route path="configuration/countries/:countryId/regions" element={<RegionsListPage />} />
        <Route path="configuration/countries/:countryId/regions/add" element={<RegionAddPage />} />
        <Route path="configuration/countries/:countryId/regions/:regionId/edit" element={<RegionEditPage />} />

        {/* Configuration - Cities */}
        <Route path="configuration/countries/:countryId/regions/:regionId/cities" element={<CitiesListPage />} />
        <Route path="configuration/countries/:countryId/regions/:regionId/cities/add" element={<CityAddPage />} />
        <Route path="configuration/countries/:countryId/regions/:regionId/cities/:cityId/edit" element={<CityEditPage />} />

        {/* Configuration - Brands */}
        <Route path="configuration/brands" element={<BrandsListPage />} />
        <Route path="configuration/brands/add" element={<BrandAddPage />} />
        <Route path="configuration/brands/:id/edit" element={<BrandEditPage />} />

        {/* Configuration - Models */}
        <Route path="configuration/brands/:brandId/models" element={<ModelsListPage />} />
        <Route path="configuration/brands/:brandId/models/add" element={<ModelAddPage />} />
        <Route path="configuration/brands/:brandId/models/:modelId/edit" element={<ModelEditPage />} />

        {/* Configuration - Vehicle Types */}
        <Route path="configuration/vehicle-types" element={<VehicleTypesListPage />} />
        <Route path="configuration/vehicle-types/add" element={<VehicleTypeAddPage />} />
        <Route path="configuration/vehicle-types/:id/edit" element={<VehicleTypeEditPage />} />

        {/* Configuration - Insurers */}
        <Route path="configuration/insurers" element={<InsurersListPage />} />
        <Route path="configuration/insurers/add" element={<InsurerAddPage />} />
        <Route path="configuration/insurers/:id/edit" element={<InsurerEditPage />} />

        {/* Configuration - Categories */}
        <Route path="configuration/categories" element={<CategoriesListPage />} />
        <Route path="configuration/categories/add" element={<CategoryAddPage />} />
        <Route path="configuration/categories/:id/edit" element={<CategoryEditPage />} />

        {/* Configuration - HS Codes */}
        <Route path="configuration/hs-codes" element={<HSCodesListPage />} />
        <Route path="configuration/hs-codes/add" element={<HSCodeAddPage />} />
        <Route path="configuration/hs-codes/:id/edit" element={<HSCodeEditPage />} />

        {/* Configuration - Shippers */}
        <Route path="configuration/shippers" element={<ShippersListPage />} />
        <Route path="configuration/shippers/add" element={<ShipperAddPage />} />
        <Route path="configuration/shippers/:id/edit" element={<ShipperEditPage />} />

        {/* Configuration - Packaging Types */}
        <Route path="configuration/packaging-types" element={<PackagingTypesListPage />} />
        <Route path="configuration/packaging-types/add" element={<PackagingTypeAddPage />} />
        <Route path="configuration/packaging-types/:id/edit" element={<PackagingTypeEditPage />} />

        {/* Configuration - Container Types */}
        <Route path="configuration/container-types" element={<ContainerTypesListPage />} />
        <Route path="configuration/container-types/add" element={<ContainerTypeAddPage />} />
        <Route path="configuration/container-types/:id/edit" element={<ContainerTypeEditPage />} />

        {/* Configuration - Currencies */}
        <Route path="configuration/currencies" element={<CurrenciesListPage />} />
        <Route path="configuration/currencies/add" element={<CurrencyAddPage />} />
        <Route path="configuration/currencies/:id/edit" element={<CurrencyEditPage />} />

        {/* Configuration - Parameters */}
        <Route path="configuration/parameters" element={<ParametersListPage />} />
        <Route path="configuration/parameters/add" element={<ParameterAddPage />} />
        <Route path="configuration/parameters/:id/edit" element={<ParameterEditPage />} />

        {/* Configuration - Payment methods availability */}
        <Route path="configuration/payment-methods" element={<PaymentMethodsSettingsPage />} />

        {/* Configuration - Corridors */}
        <Route path="configuration/corridors" element={<CorridorsListPage />} />
        <Route path="configuration/corridors/add" element={<CorridorAddPage />} />
        <Route path="configuration/corridors/:id/edit" element={<CorridorEditPage />} />

        {/* Configuration - Segments */}
        <Route path="configuration/segments" element={<SegmentsListPage />} />
        <Route path="configuration/segments/add" element={<SegmentAddPage />} />
        <Route path="configuration/segments/:id/edit" element={<SegmentEditPage />} />

        {/* Configuration - Itineraries */}
        <Route path="configuration/itineraries" element={<ItinerariesListPage />} />
        <Route path="configuration/itineraries/add" element={<ItineraryAddPage />} />
        <Route path="configuration/itineraries/:id/edit" element={<ItineraryEditPage />} />

        {/* Reference tables */}
        <Route path="references/countries" element={<PlaceholderPage />} />
        <Route path="references/divisions" element={<PlaceholderPage />} />
        <Route path="references/vehicle-types" element={<PlaceholderPage />} />
        <Route path="references/vehicle-models" element={<PlaceholderPage />} />
        <Route path="references/insurers" element={<PlaceholderPage />} />
        <Route path="references/merchandise-categories" element={<PlaceholderPage />} />
        <Route path="references/currencies" element={<PlaceholderPage />} />

        {/* Agencies */}
        <Route path="agencies" element={<PlaceholderPage />} />

        {/* User profile and settings */}
        <Route path="profile" element={<PlaceholderPage />} />
        <Route path="settings" element={<PlaceholderPage />} />
      </Route>

      {/* 404 */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate replace to="/404" />} />
    </Routes>
  );
}
